import React, { useEffect, useState } from 'react';
import { Spin, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import styled from 'styled-components';

import AgGrid from "../../../../../../components/ag-grid-table/index";
import PlacementCard from '../../../../../../components/uiComponent/placement-card';
import ShipmentLocationsPanel, { ShipmentsLayout } from '../../../../../../components/uiComponent/shipment-locations-panel';
import Button from '../../../../../../components/uiComponent/button/index';
import ProductImg from '../../../../../../assets/images/newImages/nestle.svg';
import useResponsiveTableHeight from '../../../../../../hooks/useResponsiveTableHeight';

import { ShippingWrapper } from '../style';
import { ListPlacementOptions } from '../../../../../../slices/post-shipment';

const PlacementOptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background: #F4F5F7;

    .shipments-main-row {
      flex: 1;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .options-row-header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;
    }
    .expiry-badge {
        display: inline-flex;
        align-items: center;
        grid-column-gap: 6px;
        padding: 4px 10px;
        border-radius: 6px;
        background: #FFF4E5;
        color: #8A4B00;
        font-size: 12px;
        font-weight: 600;
        width: fit-content;
    }
    .options-row {
        width: 100%;
        margin-bottom: 16px;
        &.loading-accured {
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            min-height: 120px;
            span.ant-spin-dot.ant-spin-dot-spin {
                margin-top: -20px;
            }
            .ant-spin-text {
                left: 0px;
            }
        }
    }

    .items-card {
        flex: 1;
        min-width: 0;
        min-height: 0;
        background: #fff;
        border: 1px solid #ECEFF1;
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        .items-card-header {
            display: flex;
            flex-direction: column;
            justify-content: center;
            grid-row-gap: 2px;
            /* Fixed to match .panel-header (shipment-locations-panel/index.js) so both cards'
               content starts at the same Y — see that file for why. */
            height: 58px;
            box-sizing: border-box;
            padding: 0 16px;
            border-bottom: 1px solid #ECEFF1;
            h2 {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: #16181D;
                span {
                    font-family: inherit;
                    color: #4E5969;
                    font-weight: 500;
                }
            }
            .items-card-sub {
                font-size: 12px;
                color: #808183;
            }
        }
    }

    .product-item-wrapper {
        display: flex;
        align-items: flex-start;
        grid-gap: 10px;
        img {
            width: 40px;
            height: 28px;
            object-fit: contain;
        }
        .product-item {
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
    .ag-cell {
        &:not(:first-child) {
            border-left: 1px solid #ECEFF1;
        }
    }
    footer {
        display: flex;
        justify-content: end;
        align-items: center;
        grid-gap: 16px;
    }
    .stats-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        grid-row-gap: 12px;
        margin-top: 12px;
        padding: 12px 16px;
        background: #fff;
        border: 1px solid #ECEFF1;
        border-radius: 4px;
        position: sticky;
        bottom: 0;
        z-index: 2;
        flex-shrink: 0;
        dl {
            margin: 0;
            display: flex;
            grid-column-gap: 28px;
            flex-wrap: wrap;
            grid-row-gap: 8px;
        }
        .stat {
            display: flex;
            flex-direction: column;
            grid-row-gap: 2px;
            dt {
                font-size: 11px;
                color: #808183;
            }
            dd {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: #16181D;
            }
        }
    }
    .ant-spin-nested-loading {
        position: relative;
        width: 100%;
    }

`;

const Step1 = ({ setActiveKey, onPlacementSelect, confirmingPlacementOption, handleConfirmPlacementOption, selectedPlacementOptionId, onCancelPlan, cancelingInboundPlan }) => {
  const dispatch = useDispatch();
  const { workOrderId } = useParams();

  const [selectedPlacementIndex, setSelectedPlacementIndex] = useState(0);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);

  const {
    shipmentDetails,
    placementOptions,
    listingPlacementOptions,
  } = useSelector((state) => state.postShipment || {});
  const { packingMethod, shipments } = shipmentDetails || {};

  useEffect(() => {
    if (packingMethod === 'pack_now' && isEmpty(placementOptions) && isEmpty(shipments)) {
      dispatch(ListPlacementOptions({ workOrderId }));
    }
  }, [packingMethod, workOrderId]);

  useEffect(() => {
    setSelectedPlacementIndex(0);
    setSelectedShipmentIndex(0);
  }, [placementOptions?.length]);

  useEffect(() => {
    const id = placementOptions?.[selectedPlacementIndex]?.placementOptionId || null;
    onPlacementSelect?.(id);
  }, [selectedPlacementIndex, placementOptions]);

  const { tableHeight, tableSectionRef, paginationRef } = useResponsiveTableHeight({
    minHeight: 220,
    bottomSpacing: 100,
  });
  const selectedPlacement = placementOptions?.[selectedPlacementIndex] || {};
  const selectedShipment = selectedPlacement?.shipmentDetails?.[selectedShipmentIndex] || {};
  const selectedPlacementExpiry = selectedPlacement?.expiration ? dayjs(selectedPlacement.expiration).format('M/D/YY') : null;

  if (listingPlacementOptions) {
    return (
      <ShippingWrapper className='step1' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin tip='Loading...' spinning />
      </ShippingWrapper>
    );
  }

  // If we already have confirmed shipments, show the grid
  if (!isEmpty(shipments)) {
    const totalConfirmedUnits = shipments.reduce((sum, s) => sum + (s.shipment_items_info?.reduce((sSum, i) => sSum + (i?.quantity || 0), 0) || 0), 0);
    return (
      <ShippingWrapper className='step1'>
        <ShipmentsLayout>
          <ShipmentLocationsPanel
            title="Shipments"
            hint="Units · distance"
            rowsMaxHeight={tableHeight}
            rows={shipments.map((shipment) => {
              const shipmentUnits = shipment.shipment_items_info?.reduce((sum, i) => sum + (i?.quantity || 0), 0) || 0;
              return {
                key: shipment.amazon_shipment_id,
                active: true,
                id: shipment.amazon_shipment_confirmation_id || shipment.amazon_warehouse_id,
                subLabel: shipment.amazon_warehouse_id,
                units: shipmentUnits,
                milesLabel: shipment?.miles != null ? `${shipment.miles.toLocaleString('en-US')} mi` : '— mi'
              };
            })}
          />
          <div className='items-card'>
            <div className='items-card-header'>
              <h2>Confirmed shipments</h2>
              <span className='items-card-sub'>{shipments.length} shipment{shipments.length === 1 ? '' : 's'} &middot; {totalConfirmedUnits} units</span>
            </div>
            <AgGrid
              user_referrals={shipments.flatMap(s => (s.shipment_items_info || []).map((item, index) => ({
                ...item,
                __rowId: `${s.amazon_shipment_id}-${item.id || item.msku || item.sku || index}`
              })))}
              columnDefinitions={[
                {
                  headerName: 'Items',
                  flex: 1,
                  minWidth: 200,
                  cellRenderer: ({ data }) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                      <img src={data.image_url || ProductImg} alt="Product" style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid #eceff1', borderRadius: '4px' }} />
                      <span style={{ fontSize: '12px', fontWeight: '500' }}>{data.item_name || data.msku}</span>
                    </div>
                  )
                },
                { headerName: 'SKU', field: 'msku', flex: 1 },
                { headerName: 'FNSKU', field: 'fnsku', flex: 1 },
                { headerName: 'Quantity', field: 'quantity', flex: 1 }
              ]}
              rowHeight={64}
              headerHeight={36}
              containerHeight={tableHeight}
              getRowNodeId={({ __rowId }) => __rowId}
            />
          </div>
        </ShipmentsLayout>
      </ShippingWrapper>
    );
  }

  const selectedOptionUnits = selectedPlacement?.shipmentDetails?.reduce((sum, s) => sum + (s?.items?.reduce((sSum, i) => sSum + (i?.quantity || 0), 0) || 0), 0) || 0;

  let avgMilesPerUnit = null;
  if (selectedOptionUnits > 0) {
    let weighted = 0;
    let allKnown = true;
    selectedPlacement?.shipmentDetails?.forEach((shipment) => {
      const shipmentUnits = shipment?.items?.reduce((sum, i) => sum + (i?.quantity || 0), 0) || 0;
      if (shipment?.miles == null) allKnown = false;
      else weighted += shipment.miles * shipmentUnits;
    });
    if (allKnown) avgMilesPerUnit = Math.round(weighted / selectedOptionUnits);
  }

  const selectedShipmentSkuCount = new Set(selectedShipment?.items?.map((i) => i.msku)).size;
  const selectedShipmentUnits = selectedShipment?.items?.reduce((sum, i) => sum + (i?.quantity || 0), 0) || 0;

  return (
    <ShippingWrapper className='step1'>
      <PlacementOptionWrapper>
        {selectedPlacementExpiry && (
          <div className='options-row-header'>
            <div className='expiry-badge'>This option expires {selectedPlacementExpiry}</div>
          </div>
        )}
        <div className={`options-row ${listingPlacementOptions || confirmingPlacementOption ? 'loading-accured' : ''}`}>
          <Spin wrapperClassName="placement-cards-loading" tip='Loading...' spinning={listingPlacementOptions || confirmingPlacementOption}>
            <PlacementCard
              cardsData={placementOptions?.map((opt, idx) => ({
                id: opt.placementOptionId,
                placementOption: `Placement Option ${idx + 1}`,
                shipments: opt.shipmentDetails?.length || 0,
                units: opt.items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0,
                discount: opt.discounts?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0,
                expiry: opt.expiration ? dayjs(opt.expiration).format('M/D/YY') : '',
                placementFee: opt.fees?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0
              }))}
              activePlacementIndex={selectedPlacementIndex}
              selectPlacementOption={(index) => {
                setSelectedPlacementIndex(index);
                setSelectedShipmentIndex(0);
              }}
            />
          </Spin>
        </div>

        <div className='shipments-main-row'>
        <Spin tip='Loading...' wrapperClassName="placement-cards-loading" spinning={listingPlacementOptions || confirmingPlacementOption}>
          <ShipmentsLayout>
            {!isEmpty(selectedPlacement?.shipmentDetails) && (
              <ShipmentLocationsPanel
                title="Destinations"
                rowsMaxHeight={tableHeight}
                rows={selectedPlacement.shipmentDetails.map((item, index) => {
                  const shipmentUnits = item?.items?.reduce((sum, i) => sum + (i?.quantity || 0), 0) || 0;
                  const skuCount = new Set(item?.items?.map((i) => i.msku)).size;
                  return {
                    key: item.shipmentId,
                    active: selectedShipmentIndex === index,
                    onClick: () => setSelectedShipmentIndex(index),
                    id: item?.fcId || item?.destinationCenterId || '---',
                    subLabel: `${skuCount} SKU${skuCount === 1 ? '' : 's'}`,
                    units: shipmentUnits,
                    milesLabel: item?.miles != null ? `${item.miles.toLocaleString('en-US')} mi` : '— mi'
                  };
                })}
              />
            )}
            <div className='items-card'>
              <div className='items-card-header'>
                <h2>Shipment {selectedPlacement?.shipmentDetails?.length ? selectedShipmentIndex + 1 : 0} of {selectedPlacement?.shipmentDetails?.length || 0} <span>&rarr; {selectedShipment?.fcId || selectedShipment?.destinationCenterId || '---'}</span></h2>
                <span className='items-card-sub'>{selectedShipmentSkuCount} SKU{selectedShipmentSkuCount === 1 ? '' : 's'} &middot; {selectedShipmentUnits} units &middot; {selectedShipment?.miles != null ? `${selectedShipment.miles.toLocaleString('en-US')} mi` : '— mi'}</span>
              </div>
              <div ref={tableSectionRef}>
                <AgGrid
                  key={`${selectedPlacement?.placementOptionId || selectedPlacementIndex}-${selectedShipment?.shipmentId || selectedShipmentIndex}`}
                  user_referrals={selectedShipment?.items?.map((item, index) => ({
                    ...item,
                    image_url: item.image_url || ProductImg,
                    title: item.item_name || item.msku,
                    __rowId: `${selectedShipment?.shipmentId || selectedShipmentIndex}-${item.msku || item.sku || item.asin || index}`
                  })) || []}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: false,
                    suppressRowClickSelection: true,
                    suppressMenu: true,
                    suppressMovable: true
                  }}
                  columnDefinitions={[
                    {
                      headerName: 'Items',
                      flex: 2,
                      minWidth: 250,
                      cellRenderer: ({ data }) => (
                        <div className='product-item-wrapper'>
                          <img src={data.image_url || ProductImg} alt="Product" />
                          <span className='product-item'>{data.title || '---'}</span>
                        </div>
                      )
                    },
                    { headerName: 'SKU', field: 'msku', flex: 1, minWidth: 120 },
                    { headerName: 'Quantity', field: 'quantity', flex: 1, minWidth: 100 }
                  ]}
                  rowHeight={64}
                  headerHeight={36}
                  containerHeight={tableHeight}
                  className="sku-table"
                  getRowNodeId={({ __rowId }) => __rowId}
                />
              </div>
            </div>
          </ShipmentsLayout>
        </Spin>
        </div>

        <div ref={paginationRef}>
          <div className='stats-footer'>
            <dl>
              <div className='stat'>
                <dt>Selected</dt>
                <dd>{placementOptions?.length ? `Placement Option ${selectedPlacementIndex + 1}` : '---'}</dd>
              </div>
              <div className='stat'>
                <dt>Shipments</dt>
                <dd>{selectedPlacement?.shipmentDetails?.length ?? '---'}</dd>
              </div>
              <div className='stat'>
                <dt>Units</dt>
                <dd>{selectedOptionUnits}</dd>
              </div>
              <div className='stat'>
                <dt>Avg distance per unit</dt>
                <dd>{avgMilesPerUnit != null ? `${avgMilesPerUnit.toLocaleString('en-US')} mi` : '---'}</dd>
              </div>
              <div className='stat'>
                <dt>Placement fee</dt>
                <dd>${selectedPlacement?.fees?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0}</dd>
              </div>
            </dl>
            <footer>
              {packingMethod === 'pack_now' && (
                <Button
                  text="Cancel Plan"
                  className="danger mb-0"
                  minWidth="80px"
                  size="small"
                  disabled={cancelingInboundPlan || confirmingPlacementOption}
                  onClick={onCancelPlan}
                />
              )}
              <Tooltip title={!selectedPlacementOptionId ? 'Please select a placement option' : ''} placement='top'>
                <span>
                  <Button
                    text="Confirm Placement Option"
                    className="mb-0"
                    minWidth="80px"
                    size="small"
                    disabled={confirmingPlacementOption || !selectedPlacementOptionId}
                    onClick={handleConfirmPlacementOption}
                  />
                </span>
              </Tooltip>
            </footer>
          </div>
        </div>
      </PlacementOptionWrapper>
    </ShippingWrapper>
  );
};

export default Step1;
